import React, { JSXElementConstructor } from 'react';
import { components, OptionProps } from 'react-select';

import { addTestIdToInnerProps } from './HBDropdown.utils';
import type { TDropdownSize } from './HBDropdown.types';
import HBListItem from '../HBListItem';
import { IconSpan } from './HBDropdown.styles';
import { vars } from '../../theme';
import type { HBHighlightTextProps } from '../HBHighlightText';
import type { ICustomThemeInput } from '../HBTextField/HBTextField.types';

export type HighlightSearchTextProps = Omit<
    HBHighlightTextProps,
    'children' | 'data-testid' | 'query'
> & { highlightSubtext?: boolean };

const Option = (
    props: OptionProps<any> & {
        highlightSearchText?: boolean | HighlightSearchTextProps;
        customTheme?: ICustomThemeInput;
    }
) => {
    const {
        children,
        isSelected,
        isFocused,
        isDisabled,
        innerProps,
        innerRef,
        size,
        highlightSearchText,
        customTheme
    } = props;
    const { 'data-testid': dataTestId } = innerProps;
    const { icon, subLabel, __isNew__: isNewOption } = props.data;
    const Icon = (iconProps: { color: string }) => icon(iconProps);

    const selected = isSelected && !isNewOption;
    const isMultiSelect = props.isMulti || props.selectProps.isMulti;
    const showMultiSelectCheckbox =
        isMultiSelect &&
        (props.hideSelectedOptions === false ||
            props.selectProps.hideSelectedOptions === false);
    const selectable = showMultiSelectCheckbox
        ? 'multi'
        : !isMultiSelect
        ? 'single'
        : undefined;

    const inputValue = props.selectProps?.inputValue;

    return (
        <components.Option {...props}>
            <HBListItem
                ref={innerRef}
                size={size !== 'sm' ? 'md' : 'sm'}
                subtext={subLabel}
                selectable={selectable}
                prefix={
                    icon && (
                        <IconSpan>
                            <Icon
                                color={
                                    isDisabled
                                        ? vars.colors.text.disabled
                                        : vars.colors.text.primary
                                }
                            />
                        </IconSpan>
                    )
                }
                highlightTextProps={
                    highlightSearchText && inputValue
                        ? {
                              query: inputValue,
                              ...(typeof highlightSearchText === 'object'
                                  ? highlightSearchText
                                  : undefined)
                          }
                        : undefined
                }
                selected={selected}
                data-highlighted={isFocused || undefined}
                disabled={isDisabled}
                {...innerProps}
                data-testid={`${dataTestId}-hb-list-item`}
                style={
                    customTheme?.background
                        ? {
                              ...innerProps.style,
                              backgroundColor: customTheme.background
                          }
                        : innerProps.style
                }
            >
                {children}
            </HBListItem>
        </components.Option>
    );
};

const createOptionComponent = ({
    customOptionComponent: CustomOptionComponent,
    checkIconColor,
    finalTestId,
    size,
    highlightSearchText,
    customTheme
}: {
    customOptionComponent: JSXElementConstructor<any>;
    checkIconColor?: string;
    finalTestId?: string;
    size?: TDropdownSize;
    highlightSearchText?: boolean | HighlightSearchTextProps;
    customTheme?: ICustomThemeInput;
}) => {
    return (props: OptionProps<any>) => {
        const { children } = props;

        const _props = addTestIdToInnerProps(props, finalTestId, 'option');

        if (CustomOptionComponent) {
            return (
                <components.Option {..._props}>
                    <CustomOptionComponent
                        {...props}
                        index={props.options.indexOf(props.data)}
                    >
                        {children}
                    </CustomOptionComponent>
                </components.Option>
            );
        }

        return (
            <Option
                {..._props}
                size={size}
                checkIconColor={checkIconColor}
                highlightSearchText={highlightSearchText}
                customTheme={customTheme}
            />
        );
    };
};

export default createOptionComponent;
